import type { ReactNode } from 'react';

import type { Account, SelectOption } from '~/shared/utils/types';

import {
  buildFilterSearchAriaLabel,
  volumeFilterSearchInputId,
} from '~/shared/components/searchInputMeta';

import FilterCheckboxList from '../FilterCheckboxList';
import SearchAccount from '../search/SearchAccount';
import SearchClusterName from '../search/SearchClusterName';
import SearchDiskType from '../search/SearchDiskType';
import SearchInstance from '../search/SearchInstance';
import SearchRegion from '../search/SearchRegion';
import SearchNodeGroup from '../search/SearchNodeGroup';
import SearchVolumeId from '../search/SearchVolumeId';
import SearchVolumeTags from '../search/SearchVolumeTags';
import type { CheckboxFilterOption } from '../checkboxFilterOption';

import type { VolumeFilterPanelState } from './panelState';

export type VolumeFilterSectionId =
  | 'account'
  | 'region'
  | 'attachedTo'
  | 'clusterName'
  | 'nodeGroup'
  | 'instanceId'
  | 'volumeId'
  | 'volumeTags'
  | 'capacity'
  | 'diskType';

/** Panel-state keys holding a `SelectOption[]` — i.e. everything the list sections below edit. */
type OptionListKey = Extract<
  keyof VolumeFilterPanelState,
  | 'accountFilters'
  | 'regionFilters'
  | 'infrastructureFilters'
  | 'clusterNameFilters'
  | 'nodeGroupFilters'
  | 'instanceFilters'
  | 'volumeIdFilters'
  | 'volumeTagFilters'
  | 'diskTypeFilters'
>;

interface SectionBodyProps {
  filters: SelectOption[];
  setFilters: (next: SelectOption[]) => void;
  isDisclosureOpen: boolean;
  /** Comma-joined pending account ids (or 'all') in org view; undefined otherwise. */
  accountsScope: string | undefined;
  accounts: Account[];
}

const attachedToOptions: CheckboxFilterOption[] = [
  { key: 'cluster', label: 'K8s cluster', count: 0 },
  { key: 'instance', label: 'EC2 Instances', count: 0 },
  { key: 'unattached', label: 'Unattached', count: 0 },
];

/** A section whose value is a list of selected options. */
export interface VolumeFilterListSection {
  kind: 'list';
  id: Exclude<VolumeFilterSectionId, 'capacity'>;
  title: string;
  stateKey: OptionListKey;
  /** Guards what the shared search components are allowed to write back. */
  accepts: (group: string) => boolean;
  /** Rendered only in org view, and only when accounts are available. */
  orgViewOnly?: boolean;
  render: (props: SectionBodyProps) => ReactNode;
}

/** The capacity range: one section, two API clauses, and a label badge instead of a count. */
export interface VolumeFilterCapacitySection {
  kind: 'capacity';
  id: 'capacity';
  title: string;
}

export type VolumeFilterSection = VolumeFilterListSection | VolumeFilterCapacitySection;

export interface StaticListSectionOptions {
  options: CheckboxFilterOption[];
  group: SelectOption['group'];
  showSectionSearch: boolean;
  /** DOM id key passed to `volumeFilterSearchInputId`. */
  sectionKey?: string;
  /** Section heading — drives `buildFilterSearchAriaLabel` when search is shown. */
  sectionTitle?: string;
}

/**
 * Renders a static option list (AWS regions, EBS volume types) as a checkbox section, adapting
 * `FilterCheckboxList`'s key-based API onto the `SelectOption[]` the rest of the panel speaks.
 *
 * Static lists carry no volume counts, hence `showCount={false}`.
 */
function staticListSection({
  options,
  group,
  showSectionSearch,
  sectionKey,
  sectionTitle,
}: StaticListSectionOptions) {
  return function StaticListSection({ filters, setFilters, isDisclosureOpen }: SectionBodyProps) {
    return (
      <FilterCheckboxList
        options={options}
        isDisclosureOpen={isDisclosureOpen}
        showSectionSearch={showSectionSearch}
        sectionSearchInputId={
          showSectionSearch && sectionKey
            ? volumeFilterSearchInputId(sectionKey)
            : undefined
        }
        sectionSearchAriaLabel={
          showSectionSearch && sectionTitle
            ? buildFilterSearchAriaLabel(sectionTitle)
            : undefined
        }
        showCount={false}
        isSelected={(key) => filters.some((f) => f.id === key)}
        onToggle={(key) =>
          setFilters(
            filters.some((f) => f.id === key)
              ? filters.filter((f) => f.id !== key)
              : [...filters, { id: key, name: key, group }]
          )
        }
      />
    );
  };
}

const AttachedToSection = staticListSection({
  options: attachedToOptions,
  group: 'attachedTo',
  showSectionSearch: false,
});

/**
 * The volume filter panel's sections, in render order — shared by All Volumes and Reports.
 *
 * The order mirrors the exploration view's `explorationDimensions`, including its quirk of
 * rendering Volume Type LAST, after capacity. `Accounts` leads because it scopes every
 * autocomplete below it.
 */
export const volumeFilterSections: readonly VolumeFilterSection[] = [
  {
    kind: 'list',
    id: 'account',
    title: 'Accounts',
    stateKey: 'accountFilters',
    accepts: (group) => group === 'accountId',
    orgViewOnly: true,
    render: ({ filters, setFilters, isDisclosureOpen, accounts }) => (
      <SearchAccount
        filters={filters}
        setFilters={setFilters}
        accounts={accounts}
        isDisclosureOpen={isDisclosureOpen}
      />
    ),
  },
  {
    kind: 'list',
    id: 'region',
    title: 'Region',
    stateKey: 'regionFilters',
    accepts: (group) => group === 'region',
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchRegion
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
  {
    kind: 'list',
    id: 'attachedTo',
    title: 'Attached To',
    stateKey: 'infrastructureFilters',
    accepts: (group) => group === 'attachedTo' || group === 'infrastructure',
    render: (props) => <AttachedToSection {...props} />,
  },
  {
    kind: 'list',
    id: 'clusterName',
    title: 'Cluster',
    stateKey: 'clusterNameFilters',
    accepts: (group) => group === 'clusterName',
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchClusterName
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
  {
    kind: 'list',
    id: 'nodeGroup',
    title: 'Node Group',
    stateKey: 'nodeGroupFilters',
    accepts: (group) => group === 'nodeGroupName',
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchNodeGroup
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
  {
    kind: 'list',
    id: 'instanceId',
    title: 'Instance ID',
    stateKey: 'instanceFilters',
    accepts: (group) => group === 'instanceId',
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchInstance
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
  {
    kind: 'list',
    id: 'volumeId',
    title: 'Volume ID',
    stateKey: 'volumeIdFilters',
    accepts: (group) => group === 'volumeId',
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchVolumeId
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
  {
    kind: 'list',
    id: 'volumeTags',
    title: 'Volume Tags',
    stateKey: 'volumeTagFilters',
    accepts: (group) => group.startsWith('tag:'),
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchVolumeTags
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
  { kind: 'capacity', id: 'capacity', title: 'Capacity' },
  {
    kind: 'list',
    id: 'diskType',
    title: 'Volume Type',
    stateKey: 'diskTypeFilters',
    accepts: (group) => group === 'diskType',
    render: ({ filters, setFilters, isDisclosureOpen, accountsScope }) => (
      <SearchDiskType
        filters={filters}
        setFilters={setFilters}
        isDisclosureOpen={isDisclosureOpen}
        accounts={accountsScope}
      />
    ),
  },
];

/** Fleet's sections all start collapsed — unlike exploration's, which default some open. */
export const volumeFilterSectionDefaultOpen: Record<VolumeFilterSectionId, boolean> =
  volumeFilterSections.reduce(
    (acc, s) => ({ ...acc, [s.id]: false }),
    {} as Record<VolumeFilterSectionId, boolean>
  );
